import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸', short: 'EN' },
    { code: 'ko', name: '한국어', flag: '🇰🇷', short: 'KO' },
  ];

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[1];

  const changeLanguage = async (langCode) => {
    if (langCode === i18n.language) {
      setIsOpen(false);
      return;
    }
    setIsChanging(true);
    try {
      await i18n.changeLanguage(langCode);
      localStorage.setItem('i18nextLng', langCode);
    } finally {
      setIsChanging(false);
      setIsOpen(false);
    }
  };

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`flex items-center gap-1 bg-transparent border-none py-1.5 px-2 cursor-pointer rounded-lg transition-colors duration-200 hover:bg-[#f5f5f7] active:bg-[#e8e8ed] ${
          isChanging ? 'opacity-60 pointer-events-none' : ''
        }`}
        onClick={() => !isChanging && setIsOpen(!isOpen)}
        aria-label="언어 선택"
        disabled={isChanging}
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
      >
        <span className={`text-[0.85rem] font-semibold text-[#1d1d1f] tracking-[0.02em] ${isChanging ? 'animate-pulse' : ''}`}>
          {currentLang.short}
        </span>
        <svg
          className={`transition-transform duration-200 text-[#86868b] ${isOpen ? 'rotate-180' : ''}`}
          width="10"
          height="6"
          viewBox="0 0 10 6"
        >
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute top-full right-0 max-md:right-[-0.5rem] mt-2 bg-white/[0.98] backdrop-blur-[20px] backdrop-saturate-[180%] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.05)] overflow-hidden min-w-[140px] z-[1010] animate-[dropdownFadeIn_0.2s_ease]"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`flex items-center gap-2.5 w-full py-3 px-3.5 bg-transparent border-none cursor-pointer transition-colors duration-150 text-left hover:bg-[#f5f5f7] active:bg-[#e8e8ed] ${
                i18n.language === lang.code ? 'bg-[#e8f4fd]' : ''
              }`}
              disabled={isChanging}
              style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
            >
              <span className="text-[0.8rem] font-semibold text-[#0071e3] bg-[#e8f4fd] py-0.5 px-1.5 rounded min-w-[28px] text-center shrink-0">
                {lang.short}
              </span>
              <span className="flex-1 text-[0.9rem] text-[#1d1d1f]">{lang.name}</span>
              {i18n.language === lang.code && <span className="text-[#0071e3] font-semibold">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
